/**
 * Device Detection Service
 * 
 * Generates unique device fingerprints to detect when user switches devices
 * Uses browser/OS/screen data rather than invasive tracking
 * 
 * Security: Device fingerprints are one-way hashes, not reversible
 * Privacy: No IP tracking or persistent identifiers
 */

import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import debugLogger from '../utils/debugLogger';

/**
 * Generate a deterministic device fingerprint from browser/OS characteristics
 * Same device will always generate the same fingerprint
 * Different devices will generate different fingerprints
 * 
 * @returns {Promise<string>} - Base64-encoded SHA-256 hash of device characteristics
 */
export const generateDeviceFingerprint = async () => {
  try {
    // Collect non-invasive device characteristics
    const fingerprint = {
      // Browser info
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      hardwareConcurrency: navigator.hardwareConcurrency || 'unknown',
      
      // Screen info
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      screenColorDepth: window.screen.colorDepth,
      screenPixelDepth: window.screen.pixelDepth,
      
      // Timezone
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      
      // WebGL renderer (unique per device)
      webglRenderer: getWebGLRenderer(),
      
      // Local timezone offset
      timezoneOffset: new Date().getTimezoneOffset(),
    };

    // Convert to JSON string and hash
    const fingerprString = JSON.stringify(fingerprint);
    const hash = await sha256Hash(fingerprString);
    
    return hash;
  } catch (error) {
    console.error('Error generating device fingerprint:', error);
    // Fallback: generate from basic info
    const fallback = `${navigator.userAgent}_${window.screen.width}x${window.screen.height}`;
    return await sha256Hash(fallback);
  }
};

/**
 * Get WebGL renderer string (unique identifier per GPU/browser combination)
 * @returns {string}
 */
const getWebGLRenderer = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown';
      }
    }
  } catch (e) {
    // Silent fail
  }
  return 'unknown';
};

/**
 * SHA-256 hash function using SubtleCrypto API
 * @param {string} message
 * @returns {Promise<string>} - Base64-encoded hash
 */
const sha256Hash = async (message) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(hashBuffer)));
};

/**
 * Check if user is accessing from a different device than last login
 * Compares current fingerprint with stored fingerprint in Firestore
 * 
 * @param {string} userId - Firebase user ID
 * @param {string} currentFingerprint - Current device's fingerprint
 * @returns {Promise<boolean>} - true if device switched, false if same device
 */
export const isDeviceSwitched = async (userId, currentFingerprint) => {
  try {
    const lastDeviceRef = doc(db, 'userDeviceHistory', userId, 'devices', 'lastLogin');
    const lastDeviceSnap = await getDoc(lastDeviceRef);

    if (!lastDeviceSnap.exists()) {
      // No previous login record
      return false;
    }

    const lastFingerprint = lastDeviceSnap.data().fingerprint;
    return lastFingerprint !== currentFingerprint;
  } catch (error) {
    console.error('Error checking device switch:', error);
    // On error, don't trigger device switch logic
    return false;
  }
};

/**
 * Record device login in Firestore for device switch detection
 * Stores fingerprint, timestamp, and device metadata
 * 
 * @param {string} userId - Firebase user ID
 * @param {string} fingerprint - Device fingerprint
 * @returns {Promise<void>}
 */
export const recordDeviceLogin = async (userId, fingerprint) => {
  try {
    const deviceData = {
      fingerprint,
      lastLogin: serverTimestamp(),
      browser: getBrowserInfo(),
      os: getOSInfo(),
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    // Store in lastLogin reference for quick comparison
    const lastLoginRef = doc(db, 'userDeviceHistory', userId, 'devices', 'lastLogin');
    await setDoc(lastLoginRef, deviceData, { merge: true });

    // Also store in timestamped history for audit trail (optional)
    const timestamp = Date.now();
    const historyRef = doc(db, 'userDeviceHistory', userId, 'devices', `login_${timestamp}`);
    await setDoc(historyRef, deviceData);

    debugLogger.log('✅ Device login recorded:', { userId, fingerprint });
  } catch (error) {
    console.error('Error recording device login:', error);
    // Non-critical, don't throw
  }
};

/**
 * Get stored device fingerprint for user (last login)
 * @param {string} userId
 * @returns {Promise<string|null>}
 */
export const getStoredDeviceFingerprint = async (userId) => {
  try {
    const lastDeviceRef = doc(db, 'userDeviceHistory', userId, 'devices', 'lastLogin');
    const lastDeviceSnap = await getDoc(lastDeviceRef);

    if (lastDeviceSnap.exists()) {
      return lastDeviceSnap.data().fingerprint || null;
    }
    return null;
  } catch (error) {
    console.error('Error getting stored device fingerprint:', error);
    return null;
  }
};

/**
 * Extract browser name from user agent
 * @returns {string}
 */
const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  if (ua.indexOf('Firefox') > -1) return 'Firefox';
  if (ua.indexOf('Chrome') > -1) return 'Chrome';
  if (ua.indexOf('Safari') > -1) return 'Safari';
  if (ua.indexOf('Edge') > -1) return 'Edge';
  return 'Unknown';
};

/**
 * Extract OS name from user agent
 * @returns {string}
 */
const getOSInfo = () => {
  const ua = navigator.userAgent;
  if (ua.indexOf('Win') > -1) return 'Windows';
  if (ua.indexOf('Mac') > -1) return 'macOS';
  if (ua.indexOf('Linux') > -1) return 'Linux';
  if (ua.indexOf('Android') > -1) return 'Android';
  if (ua.indexOf('iPhone') > -1 || ua.indexOf('iPad') > -1) return 'iOS';
  return 'Unknown';
};

/**
 * Get device history for a user (for debugging/audit)
 * @param {string} userId
 * @returns {Promise<Array>}
 */
export const getDeviceHistory = async (userId) => {
  try {
    // Note: This would require reading multiple documents
    // For now, we only store lastLogin for performance
    const lastDeviceRef = doc(db, 'userDeviceHistory', userId, 'devices', 'lastLogin');
    const lastDeviceSnap = await getDoc(lastDeviceRef);

    if (lastDeviceSnap.exists()) {
      return [lastDeviceSnap.data()];
    }
    return [];
  } catch (error) {
    console.error('Error getting device history:', error);
    return [];
  }
};

/**
 * Clear stored device fingerprint (for testing or manual reset)
 * @param {string} userId
 * @returns {Promise<void>}
 */
export const clearStoredFingerprint = async (userId) => {
  try {
    const lastDeviceRef = doc(db, 'userDeviceHistory', userId, 'devices', 'lastLogin');
    // Delete by setting to empty, or use deleteDoc if available
    await setDoc(lastDeviceRef, {}, { merge: true });
    debugLogger.log('✅ Stored fingerprint cleared for:', userId);
  } catch (error) {
    console.error('Error clearing stored fingerprint:', error);
  }
};
